import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import {
  formatDistance,
  formatDuration,
  formatKmh,
  formatDateTime,
} from "../../utils/locationAnalytics";

/**
 * A row of six dense, monospace-style stat cards that summarize the current
 * point window. Designed to match Varna's existing MUI card chrome but
 * dialed toward a "mission control" readout.
 */
const StatTile = ({ label, value, sub, accent = "primary" }) => (
  <Card
    sx={{
      flex: "1 1 160px",
      minWidth: 160,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "3px",
        height: "100%",
        background:
          accent === "primary"
            ? "linear-gradient(180deg, #ff3b4e, #8a1125)"
            : accent === "ok"
            ? "linear-gradient(180deg, #58f0be, #0fa070)"
            : accent === "warn"
            ? "linear-gradient(180deg, #ffd770, #d97706)"
            : "linear-gradient(180deg, #10324a, #0a2235)",
      },
    }}
  >
    <CardContent sx={{ py: 2, pl: 2.5 }}>
      <Typography
        variant="overline"
        sx={{
          color: "#5f6877",
          letterSpacing: "0.12em",
          fontWeight: 600,
          fontSize: "10.5px",
          lineHeight: 1.4,
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Outfit", "DM Sans", sans-serif',
          fontWeight: 700,
          fontSize: "22px",
          color: "#10324a",
          lineHeight: 1.15,
          mt: 0.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          sx={{
            color: "#7a8294",
            fontSize: "12px",
            mt: 0.4,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export const MovementStatCards = ({ summary, stopCount }) => {
  if (!summary) return null;

  const distance = formatDistance(summary.totalDistanceMeters);
  const duration = formatDuration(summary.durationMs);
  const avgSpeed = formatKmh(summary.averageSpeedKmh);
  const topSpeed = formatKmh(summary.topSpeedKmh);
  const firstAt = formatDateTime(summary.first?.recorded_at);
  const lastAt = formatDateTime(summary.last?.recorded_at);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <StatTile
        label="Sträcka"
        value={distance}
        sub={`${summary.count} punkter`}
      />
      <StatTile label="Varaktighet" value={duration} accent="secondary" />
      <StatTile label="Snitthastighet" value={avgSpeed} />
      <StatTile label="Topphastighet" value={topSpeed} accent="warn" />
      <StatTile
        label="Stopp"
        value={String(stopCount ?? 0)}
        sub={stopCount ? "markerade på kartan" : "inga identifierade"}
        accent="warn"
      />
      <StatTile
        label="Tidsfönster"
        value={firstAt}
        sub={`till ${lastAt}`}
        accent="ok"
      />
    </Box>
  );
};

export default MovementStatCards;
